/**
 * CustomerListsChips — P0-6 FASE 2G+ · Chip section liste per scheda cliente.
 * Sarabi CRM è la SOURCE OF TRUTH delle liste (nessun sync WABA).
 * Used in AdminCustomerDetail e nel bottom sheet Staff clienti.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { X, Plus, Search } from "lucide-react";
import { hasPermission } from "@/lib/permissions";
import { useAuth } from "@/lib/auth";

export default function CustomerListsChips({ customerId, className = "" }) {
  const { user } = useAuth();
  const canWrite = hasPermission(user, "crm.customers.write");
  const [lists, setLists] = useState([]);       // liste del customer
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/customers/${customerId}/lists`);
      setLists(r.data?.lists || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const removeFromList = async (listId) => {
    try {
      await api.delete(`/admin/customers/${customerId}/lists/${listId}`);
      toast.success("Rimosso dalla lista");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className={className} data-testid="customer-lists-chips">
      <div className="text-xs eyebrow mb-2">Liste</div>
      <div className="flex flex-wrap gap-1.5 items-center">
        {loading && <span className="text-xs text-ink-400">…</span>}
        {!loading && lists.length === 0 && (
          <span className="text-xs text-ink-400" data-testid="lists-empty">Nessuna lista</span>
        )}
        {lists.map((l) => (
          <span key={l.id} data-testid={`list-chip-${l.id}`}
                className="inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 border font-medium"
                style={{ borderColor: l.color || "#94a3b8",
                          background: (l.color || "#94a3b8") + "1a",
                          color: l.color || "#1e293b" }}>
            {l.name}
            {canWrite && (
              <button onClick={() => removeFromList(l.id)}
                       data-testid={`list-chip-remove-${l.id}`}
                       className="opacity-60 hover:opacity-100">
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}
        {canWrite && (
          <button onClick={() => setPickerOpen(true)}
                   data-testid="btn-add-to-list"
                   className="inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1
                              border border-dashed border-bronze-400 text-bronze-800
                              hover:bg-bronze-50">
            <Plus className="w-3 h-3" /> Aggiungi a lista
          </button>
        )}
      </div>

      {pickerOpen && (
        <ListPickerModal
          onClose={() => setPickerOpen(false)}
          currentIds={new Set(lists.map(l => l.id))}
          onAdd={async (listId) => {
            try {
              await api.post(`/admin/customers/${customerId}/lists`,
                              { list_ids: [listId] });
              toast.success("Aggiunto");
              load();
            } catch (e) { toast.error(formatApiError(e)); }
          }}
        />
      )}
    </div>
  );
}


export function ListPickerModal({ onClose, currentIds = new Set(), onAdd,
                                    multiSelect = false, onMultiSelect,
                                    title = "Aggiungi a lista",
                                    preselected = new Set() }) {
  const { user } = useAuth();
  const canWrite = hasPermission(user, "crm.customers.write");
  const [lists, setLists] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [selected, setSelected] = useState(new Set(preselected));

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/crm/lists${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setLists(r.data?.lists || []);
    } catch (e) { /* silent */ }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const createList = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/admin/crm/lists", { name });
      toast.success(data.already_existed ? "Lista esistente" : "Lista creata");
      setNewName(""); setCreating(false);
      load();
      if (!multiSelect) onAdd?.(data.id);
      else setSelected((s) => new Set([...s, data.id]));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = (id) => {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="list-picker-modal" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-md w-full p-5 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-xl">{title}</h3>
          <button onClick={onClose} className="opacity-60 hover:opacity-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-3 w-4 h-4 text-ink-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Cerca lista…" data-testid="list-picker-search"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-sand-300" />
        </div>
        <div className="flex-1 overflow-y-auto space-y-1 mb-3">
          {lists.map((l) => {
            const isCurrent = currentIds.has(l.id);
            const isSel = selected.has(l.id);
            return (
              <button key={l.id}
                       data-testid={`list-picker-row-${l.id}`}
                       onClick={() => {
                         if (multiSelect) toggle(l.id);
                         else if (!isCurrent) onAdd?.(l.id) & onClose?.();
                       }}
                       disabled={!multiSelect && isCurrent}
                       className={`w-full text-left rounded-xl border p-2.5 flex items-center gap-2 transition ${
                         isSel ? "border-emerald-500 bg-emerald-50"
                               : isCurrent ? "border-sand-200 bg-sand-50 opacity-60"
                                           : "border-sand-200 hover:border-bronze-400"}`}>
                {multiSelect && (
                  <input type="checkbox" checked={isSel} readOnly
                          data-testid={`list-picker-check-${l.id}`} />
                )}
                <span className="inline-block w-3 h-3 rounded-full"
                       style={{ background: l.color || "#94a3b8" }} />
                <span className="flex-1 font-medium">{l.name}</span>
                <span className="text-xs text-ink-500">{l.members_count} clienti</span>
                {isCurrent && !multiSelect && (
                  <span className="text-[10px] text-emerald-700">Già presente</span>
                )}
              </button>
            );
          })}
          {lists.length === 0 && !creating && (
            <div className="text-center text-sm text-ink-400 py-4">Nessuna lista trovata</div>
          )}
        </div>
        {canWrite && (
          <div className="border-t border-sand-200 pt-3">
            {!creating ? (
              <Button variant="outline" size="sm" onClick={() => setCreating(true)}
                       data-testid="btn-new-list" className="rounded-full">
                <Plus className="w-3 h-3 mr-1" /> Nuova lista
              </Button>
            ) : (
              <div className="flex gap-2">
                <input value={newName} onChange={(e) => setNewName(e.target.value)}
                        placeholder="Nome lista"
                        autoFocus data-testid="input-new-list-name"
                        className="flex-1 px-3 py-2 rounded-lg border border-sand-300"
                        onKeyDown={(e) => e.key === "Enter" && createList()} />
                <Button size="sm" onClick={createList} data-testid="btn-create-list"
                         className="bg-bronze-600 text-white rounded-full">Crea</Button>
                <Button size="sm" variant="outline" onClick={() => setCreating(false)}>Annulla</Button>
              </div>
            )}
          </div>
        )}
        {multiSelect && (
          <div className="border-t border-sand-200 pt-3 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Annulla</Button>
            <Button size="sm"
                     onClick={() => { onMultiSelect?.(Array.from(selected)); onClose(); }}
                     data-testid="btn-list-picker-confirm"
                     disabled={selected.size === 0}
                     className="bg-bronze-600 text-white rounded-full">
              Conferma ({selected.size})
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
